import { useCallback, useEffect, useState } from 'react'
import { MinimalAlert, MinimalButton, MinimalCard, MinimalFieldGrid, MinimalFormSection, MinimalInput } from '@ovasabi/ui-minimal'
import * as S from './OpsStyle'
import { commandPayload, commands } from '../../lib/runtime'
import { confirmedUpsert, type StoreRecord } from '../choosechow/chefShared'
import { useMealCategories } from '../choosechow/mealCategories'

export const OpsCategoriesPage = () => {
  const categories = useMealCategories()
  const [requests, setRequests] = useState<StoreRecord[]>([])
  const [label, setLabel] = useState('')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const reload = useCallback(async () => {
    try {
      setRequests(commandPayload(await commands.menu.categoryRequests()).requests as StoreRecord[])
      setError('')
    } catch (reason) { setError(reason instanceof Error ? reason.message : 'Could not load meal categories.') }
  }, [])
  useEffect(() => { void reload() }, [reload])
  // The projection re-delivers a new category on its next sweep; show it now.
  const manage = async (payload: Record<string, unknown>) => {
    const saved = commandPayload(await commands.menu.manageCategory(payload))
    if (typeof saved.id === 'string' && saved.id) confirmedUpsert('menu.meal_categories', saved)
  }
  const run = async (action: () => Promise<unknown>) => {
    setBusy(true)
    try { await action(); await reload(); setLabel('') }
    catch (reason) { setError(reason instanceof Error ? reason.message : 'Category change failed.') }
    finally { setBusy(false) }
  }
  return <div style={{ display: 'grid', gap: 16 }}>
    <S.H1>Meal categories</S.H1>
    <MinimalFormSection title="Manage categories" description="Categories are shared across this organization. Only operations can add or approve categories.">
      {error && <MinimalAlert tone="danger">{error}</MinimalAlert>}
      <MinimalFieldGrid columns={2}>
        <MinimalInput required label="Category name" hint="Up to 100 characters." maxLength={100} value={label} onChange={(event) => setLabel(event.target.value)} />
        <MinimalInput label="Review note (optional)" maxLength={1000} value={note} onChange={(event) => setNote(event.target.value)} />
      </MinimalFieldGrid>
      <MinimalButton disabled={busy || label.trim().length < 2} onClick={() => void run(() => manage({ label }))}>Add category</MinimalButton>
      <p>{categories.map((category) => String(category.label)).join(' · ') || 'No categories loaded.'}</p>
    </MinimalFormSection>
    <MinimalFormSection title="Chef category requests" description="Approve a request to make the category available in dish selectors.">
      {requests.length === 0 && <p>No category requests.</p>}
      {requests.map((request) => <MinimalCard key={String(request.id)} padding="sm" role="group" aria-label={`Category request: ${String(request.label)}`}>
        <strong>{String(request.label)}</strong><p>{String(request.reason || 'No additional reason.')}</p><p>Status: {String(request.status)}</p>
        {request.status === 'pending' && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <MinimalButton disabled={busy} onClick={() => void run(() => manage({ label: String(request.label), request_id: String(request.id), review_note: note }))}>Approve category</MinimalButton>
          <MinimalButton disabled={busy} variant="secondary" onClick={() => void run(() => commands.menu.declineCategory(String(request.id), note))}>Decline request</MinimalButton>
        </div>}
      </MinimalCard>)}
    </MinimalFormSection>
  </div>
}
